import CardData from '../dashboard/cardData';
import {
  Activity,
  ArrowRight,
  Briefcase,
  Calendar,
  Clipboard,
  DocumentAdd,
  DocumentCopy,
  DocumentText,
  Edit,
  LinearCloseCirlce,
  TextBlock,
  User,
} from '../icons';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  getLocalDateAndTime,
  getMatterIconClass,
  getMatterLabel,
  rgbToRgba,
  workTimeFallBackColor,
} from '../../constants/utilityFunctions';
import { useEffect, useMemo, useState } from 'react';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { LOCAL_STORAGE_NAMES, ROUTES, ThemeMode } from '../../constants/constants';
import { ITransaction } from '../../interface/transaction';
import { useTransaction } from '../../context/TransactionContext';
import RenderTransactionRestrictions from '../dashboard/addTransaction/renderTransactionRestrictions';
import { useFetchWorkTimeDetail } from '../../hooks/useFetchWorkTimeDetail';
import { useDeleteWorkTime } from '../../hooks/useDeleteWorkTime';
import { PrimaryCard } from '../../assets/styles/components/dashboard.styled';
import { Button, CardCta, Divider, Paragraph } from '../Ant';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useWorkTime } from '../../context/WorkTimeContext';
import { useLoading } from '../../context/LoadingContext';
import DescriptionModal from '../descriptionModal/descriptionModal';
import CustomModal from '../informationPopup/customModal';
import WorkTimeDeleteModal, { DeleteModalType } from './workTimeDeleteModal';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import { themes } from '../config/variables';
import { hasGoodContrast } from '../../helpers/colorContrastRatioHelper';
import { IWorkTime } from '../../interface';
import { TransactionStatus } from '../../constants/transaction';
import SkeletonEntityCard from '../common/skeleton/skeletonEntityCard';
import DetailScrollArea from '../detailScrollArea/detailScrollArea';
import { getViewLabel } from '../../helpers/viewLabelHelper';
import { HoursDurationDisplay } from '../hoursDurationDisplay/hoursDurationDisplay';

const WorkTimeDetail = () => {
  const storedTheme = localStorage.getItem(LOCAL_STORAGE_NAMES.theme);
  const { id } = useParams();
  const { push, goBack } = useNavigationStack();
  const { t } = useTranslation();
  const { setTitle } = usePage();
  const { trackPage } = useAnalytics();
  const { getAppLanguageCode } = useLanguage();
  const { updateTransaction } = useTransaction();
  const { updateWorkTime } = useWorkTime();
  const { setIsLoading } = useLoading();
  const [deleteModal, setDeleteModal] = useState(false);

  const [selectedTransaction, setSelectedTransaction] = useState<ITransaction>();
  const [isShowMoreModalOpen, setIsShowMoreModalOpen] = useState<boolean>(false);
  const [
    workTimeInvoicedThroughActivityModalVisible,
    setWorkTimeInvoicedThroughActivityModalVisible,
  ] = useState<boolean>(false);
  const [showMoreContent, setShowMoreContent] = useState<
    { title: string; content: string } | undefined
  >(undefined);
  const deleteWorkTimeMutation = useDeleteWorkTime();
  const handleDeleteWithoutTransaction = () =>
    handleDeleteWorkTime({ deleteLinkedTransaction: false });
  const handleDeleteWithTransaction = () => handleDeleteWorkTime({ deleteLinkedTransaction: true });

  const {
    data: workTime,
    isLoading: isLoadingWorkTime,
    refetch: refetchWorkTime,
  } = useFetchWorkTimeDetail({
    id,
    relations: [
      'matter',
      'activity',
      'transaction',
      'workTimeType',
      'company',
      'privatePerson',
      'workTimeUser',
      'activity.activityType',
    ],
    fields: [
      'startTime',
      'endTime',
      'workTimeUserId',
      'activityId',
      'duration',
      'subject',
      'description',
      'transactionId',
      'matterId',
      'workTimeTypeId',
    ],
  });

  useEffect(() => {
    trackPage(AnalyticsPage.WORK_TIME_DETAIL);
  }, [trackPage]);
  const translatedStatus =
    workTime?.workTimeType?.translations?.find((t) => t.language === getAppLanguageCode())
      ?.translation ?? t('worktime:typeNotDefined');
  const handleBack = () => {
    goBack();
  };
  const handleConfirmDelete = () => {
    setDeleteModal(true);
  };
  const handleDeleteWorkTime = ({
    deleteLinkedTransaction,
  }: {
    deleteLinkedTransaction: boolean;
  }) => {
    deleteWorkTimeMutation.mutate(
      { id: id ?? '', deleteOpenLinkedTransaction: deleteLinkedTransaction },
      {
        onSettled() {
          setDeleteModal(false);
          handleBack();
        },
      },
    );
  };
  const handleTaskToTransaction = (transaction?: ITransaction) => {
    if (transaction) {
      updateTransaction({
        matterId: workTime?.matterId,
        matter: workTime?.matter,
        subject: workTime?.subject,
        currentSubject: workTime?.subject,
        isManualUnitPrice: false,
        shouldLoadServerPrice: false,
        restrictPriceUpdate: true,
        principalCompany: workTime?.company,
        principalPrivatePerson: workTime?.privatePerson,
        workedHours: workTime?.duration,
        billableQuantity: workTime?.duration,
        workTimeId: workTime?.id,
        transactionDate: dayjs(workTime?.startTime).format('YYYY-MM-DD'),
        overrideWarnings: true,
      } as ITransaction);
      push(ROUTES.ADD_EDIT_TRANSACTION);
    } else {
      setSelectedTransaction(undefined);
    }
  };
  useEffect(() => {
    if (workTime) {
      setTitle(
        <AddEditTransactionHeader
          handleBack={handleBack}
          title={workTime?.subject}
          subTitle={t('worktime:workTime')}
          handleDelete={handleConfirmDelete}
        />,
      );
    }
  }, [workTime]);

  const handleGoToMatter = () => {
    push(ROUTES.MATTER_DETAIL + `/${workTime?.matter?.id}`);
  };
  const handleGoToTransaction = () => {
    push(ROUTES.TRANSACTION_VIEW + `/${workTime?.transaction?.id}`);
  };
  const handleGoToActivity = () => {
    push(ROUTES.TASKS_DETAIL + `/${workTime?.activity?.id}`);
  };
  const handleEditWorkTime = async () => {
    updateWorkTime(null);
    setIsLoading(true);
    try {
      await refetchWorkTime();
    } finally {
      setIsLoading(false);
    }
    push(ROUTES.ADD_EDIT_WORK_TIME + `/${workTime?.id}`);
  };
  const handleClose = () => {
    setIsShowMoreModalOpen(false);
  };
  const handleCopyWorkTime = () => {
    if (!workTime) return;
    updateWorkTime({
      ...workTime,
      id: undefined,
    } as unknown as IWorkTime);
    push(ROUTES.ADD_EDIT_WORK_TIME);
  };
  const workTimeColor =
    workTime?.workTimeType?.color ?? workTimeFallBackColor(storedTheme as ThemeMode);
  const isLightTheme = storedTheme === ThemeMode.light.toLowerCase();
  const lightBackgroundColor = `rgb(${themes['light']['clr-container-bg']})`;
  let borderColor = workTimeColor;
  if (isLightTheme && !hasGoodContrast(workTimeColor, lightBackgroundColor)) {
    borderColor = '#E3E3E3';
  }
  const deleteModalType = useMemo(() => {
    if (!workTime?.transactionId) {
      return DeleteModalType.NO_TRANSACTION;
    }

    const transactionStatus = workTime.transaction?.status;
    const canDeleteTransaction = transactionStatus === TransactionStatus.open;
    return canDeleteTransaction
      ? DeleteModalType.DELETABLE_TRANSACTION
      : DeleteModalType.NOT_OPEN_TRANSACTION;
  }, [workTime?.transactionId, workTime?.transaction?.status]);
  const handleCancelDelete = () => setDeleteModal(false);
  if (!workTime && !isLoadingWorkTime) return null;
  if (isLoadingWorkTime) {
    return (
      <DetailScrollArea>
        <SkeletonEntityCard
          rows={[1, 1, 2, 1, 1]}
          withSubcontentOnFirstRow
          className="px-10 py-10 h-full"
        />
      </DetailScrollArea>
    );
  }
  return (
    <DetailScrollArea>
      <PrimaryCard
        data-testid="work-time-detail"
        $isDetail
        className={`primary-card task-card d-flex flex-col gap-10 h-full `}
        style={{
          border: `1px solid ${borderColor}`,
        }}
      >
        <div className="d-flex justify-between w-full items-center">
          <div className="d-flex gap-10 items-center transaction-status-active">
            <div
              data-testid="work-time-status-chip"
              className={`status-chip d-flex justify-center items-center`}
              style={{
                backgroundColor: rgbToRgba(workTimeColor, 0.2) as string,
              }}
            >
              {translatedStatus}
            </div>
          </div>
        </div>
        <Divider />
        <div className="d-flex flex-col gap-10 flex-grow">
          <CardData
            type="work-time-subject"
            icon={<Clipboard />}
            title={t('common:subject')}
            content={workTime?.subject ?? '-'}
            contentclass="lineclamp-1"
            moreAction={
              <Button
                type="text"
                data-testid="work-time-subject-show-more"
                className="text-cta show-more"
                onClick={() => {
                  setShowMoreContent({
                    title: t('common:subject'),
                    content: workTime?.subject ?? '',
                  });
                  setIsShowMoreModalOpen(true);
                }}
              >
                {t('common:more')}
              </Button>
            }
          />
          <div className="d-flex justify-between">
            <CardData
              type="work-time-start-time"
              icon={<Calendar />}
              title={t('worktime:startTime')}
              content={
                workTime?.startTime ? getLocalDateAndTime(new Date(workTime?.startTime)) : '-'
              }
            />
            <CardData
              type="work-time-end-time"
              icon={<Calendar />}
              title={t('worktime:endTime')}
              content={workTime?.endTime ? getLocalDateAndTime(new Date(workTime?.endTime)) : '-'}
            />
          </div>
          <div className="d-flex justify-between">
            <HoursDurationDisplay
              type="work-time-duration"
              title={t('task:duration')}
              durationInHours={workTime?.duration ?? 0}
            />
            <CardData
              type="work-time-author"
              icon={<User />}
              title={t('common:author')}
              content={workTime?.workTimeUser?.fullName ?? '-'}
            />
          </div>
          <CardData
            type="work-time-description"
            icon={<TextBlock />}
            title={t('common:description')}
            content={workTime?.description ?? '-'}
            contentclass="lineclamp-2 fw-400"
            moreAction={
              <Button
                type="text"
                data-testid="work-time-description-show-more"
                className="text-cta show-more"
                onClick={() => {
                  setShowMoreContent({
                    title: t('worktime:description'),
                    content: workTime?.description ?? '',
                  });
                  setIsShowMoreModalOpen(true);
                }}
              >
                {t('common:more')}
              </Button>
            }
          />
          <Divider />
          <CardData
            type="work-time-matter"
            icon={
              <Briefcase className={getMatterIconClass(workTime?.matter?.invoiceableType ?? '')} />
            }
            title={getMatterLabel(t, workTime?.matter?.invoiceableType ?? '')}
            content={
              workTime?.matter &&
              (workTime?.company?.name ?? workTime?.privatePerson?.fullName ?? '')
            }
            subcontent={
              (workTime?.matter?.matterNumber ?? '') + ' - ' + (workTime?.matter?.subject ?? '')
            }
            contentclass="lineclamp-1"
            subcontentclass="lineclamp-1"
            headingcontent={
              workTime?.matter && (
                <Button
                  type="text"
                  data-testid="work-time-matter-show-more"
                  icon={<ArrowRight />}
                  iconPosition="end"
                  className="text-cta"
                  onClick={handleGoToMatter}
                >
                  {getViewLabel(t, 'matter:matter')}
                </Button>
              )
            }
          />
          <CardData
            type="work-time-transaction"
            icon={<DocumentText />}
            title={t('transaction:transaction')}
            content={workTime?.transaction?.subject ?? '-'}
            contentclass="lineclamp-1"
            headingcontent={
              workTime?.transaction && (
                <Button
                  type="text"
                  data-testid="work-time-transaction-show-more"
                  icon={<ArrowRight />}
                  iconPosition="end"
                  className="text-cta"
                  onClick={handleGoToTransaction}
                >
                  {getViewLabel(t, 'transaction:transaction')}
                </Button>
              )
            }
          />

          <CardData
            type="work-time-activity"
            icon={<Activity />}
            title={t('worktime:activity')}
            content={workTime?.activity?.subject ?? '-'}
            contentclass="lineclamp-1"
            headingcontent={
              workTime?.activity && (
                <Button
                  type="text"
                  data-testid="work-time-activity-show-more"
                  icon={<ArrowRight />}
                  iconPosition="end"
                  className="text-cta"
                  onClick={handleGoToActivity}
                >
                  {getViewLabel(t, 'task:task')}
                </Button>
              )
            }
          />
        </div>

        <div className="d-flex flex-col gap-10 w-full">
          <Divider />
          <div className="d-flex gap-10">
            {!workTime?.transactionId && (
              <CardCta
                data-testid="work-time-to-transaction"
                onClick={() => {
                  if (workTime?.activity?.activityType?.workTimeInvoicedThroughActivity) {
                    setWorkTimeInvoicedThroughActivityModalVisible(true);
                    return;
                  }
                  if (workTime?.matter) {
                    setSelectedTransaction({
                      matter: workTime?.matter,
                      matterId: workTime?.matterId,
                    } as ITransaction);
                  } else {
                    handleTaskToTransaction({} as ITransaction);
                  }
                }}
              >
                <DocumentAdd />
                <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                  {t('task:taskToTransaction')}
                </Paragraph>
              </CardCta>
            )}
            <CardCta data-testid="copy-transaction-button" onClick={handleCopyWorkTime}>
              <DocumentCopy />
              <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                {t('worktime:copyWorkTime')}
              </Paragraph>
            </CardCta>
            <CardCta data-testid="work-time-edit" onClick={handleEditWorkTime}>
              <Edit />
              <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                {t('worktime:editWorkTime')}
              </Paragraph>
            </CardCta>
          </div>
        </div>
      </PrimaryCard>
      <RenderTransactionRestrictions
        transaction={selectedTransaction}
        onSelectTransaction={handleTaskToTransaction}
      />
      {/* Stays mounted through its closing animation, which is when antd hands focus back. */}
      <DescriptionModal
        title={showMoreContent?.title ?? ''}
        description={showMoreContent?.content ?? ''}
        open={isShowMoreModalOpen}
        onClose={handleClose}
      />
      <CustomModal
        visible={workTimeInvoicedThroughActivityModalVisible}
        title={t('common:error')}
        subdescription={t('worktime:workTimeInvoicedThroughActivityTitle')}
        confirmText={t('common:ok')}
        icon={<LinearCloseCirlce />}
        onClose={() => {
          setWorkTimeInvoicedThroughActivityModalVisible(false);
        }}
        onConfirm={() => {
          setWorkTimeInvoicedThroughActivityModalVisible(false);
        }}
      />
      <WorkTimeDeleteModal
        deleteModal={deleteModal}
        deleteModalType={deleteModalType}
        handleCancelDelete={handleCancelDelete}
        handleDeleteWithTransaction={handleDeleteWithTransaction}
        handleDeleteWithoutTransaction={handleDeleteWithoutTransaction}
      />
    </DetailScrollArea>
  );
};

export default WorkTimeDetail;
